import ReactCodeMirror from "@uiw/react-codemirror";
import { yaml } from "@codemirror/lang-yaml";
import SwaggerLayoutPlugin from "components/SwaggerUiLayout";
import { useEffect, useRef, useState } from "react";
import { Container, Header, Icon, List, Segment } from "semantic-ui-react";
import { store } from "services/state";

import SwaggerUI from "swagger-ui";
import "swagger-ui/dist/swagger-ui.css";
import * as YAML from "yaml";
import { Link } from "react-router";

function RestApis() {
  const containerRef = useRef();
  const [showExampleSnippet, setShowExampleSnippet] = useState(true);
  const [showExampleUi, setShowExampleUi] = useState(true);

  const api = `openapi: 3.0.0
info:
  title: Simple User API
  description: |-
    This API allows users to retrieve information about registered
    users and manage user data. It provides endpoints to get a list of users and
    retrieve detailed information about a specific user using their unique ID.
    This API is designed to be simple, efficient, and easy to use for developers
    integrating user management into their applications.
  version: 1.0.1
servers:
  - url: https://api.example.com/v1
    description: Production server
paths:
  /users:
    get:
      summary: Retrieve a list of users
      description: Returns a list of users in the system.
      operationId: getUsers
      tags:
        - Users
      responses:
        "200":
          description: A list of users.
          content:
            application/json:
              schema:
                type: array
                items:
                  type: object
                  properties:
                    id:
                      type: string
                      description: The unique identifier for the user.
                    name:
                      type: string
                      description: The name of the user.
                    email:
                      type: string
                      format: email
                      description: The email address of the user.
                  required:
                    - id
                    - name
                    - email
        "401":
          description: Unauthorized - Invalid or missing authentication.
          content:
            application/json:
              schema:
                type: object
                properties:
                  code:
                    type: integer
                    description: Error code.
                  message:
                    type: string
                    description: Error message.
                required:
                  - code
                  - message
        "500":
          description: Server error
          content:
            application/json:
              schema:
                type: object
                properties:
                  code:
                    type: integer
                    description: Error code.
                  message:
                    type: string
                    description: Error message.
                required:
                  - code
                  - message
  /users/{userId}:
    get:
      summary: Retrieve a single user by ID
      description: Returns a single user by their unique ID.
      operationId: getUserById
      tags:
        - Users
      parameters:
        - name: userId
          in: path
          required: true
          description: The ID of the user to retrieve.
          schema:
            type: string
      responses:
        "200":
          description: A single user.
          content:
            application/json:
              schema:
                type: object
                properties:
                  id:
                    type: string
                    description: The unique identifier for the user.
                  name:
                    type: string
                    description: The name of the user.
                  email:
                    type: string
                    format: email
                    description: The email address of the user.
                required:
                  - id
                  - name
                  - email
        "401":
          description: Unauthorized - Invalid or missing authentication.
          content:
            application/json:
              schema:
                type: object
                properties:
                  code:
                    type: integer
                    description: Error code.
                  message:
                    type: string
                    description: Error message.
                required:
                  - code
                  - message
        "404":
          description: User not found
          content:
            application/json:
              schema:
                type: object
                properties:
                  code:
                    type: integer
                    description: Error code.
                  message:
                    type: string
                    description: Error message.
                required:
                  - code
                  - message
        "500":
          description: Server error
          content:
            application/json:
              schema:
                type: object
                properties:
                  code:
                    type: integer
                    description: Error code.
                  message:
                    type: string
                    description: Error message.
                required:
                  - code
                  - message
components:
  schemas:
    User:
      type: object
      properties:
        id:
          type: string
          description: The unique identifier for the user.
        name:
          type: string
          description: The name of the user.
        email:
          type: string
          format: email
          description: The email address of the user.
      required:
        - id
        - name
        - email
    Error:
      type: object
      properties:
        code:
          type: integer
          description: Error code.
        message:
          type: string
          description: Error message.
      required:
        - code
        - message
`;

  useEffect(() => {
    store.api = YAML.parse(api);
    SwaggerUI({
      plugins: [SwaggerLayoutPlugin],
      domNode: containerRef.current,
      spec: YAML.parse(api),
    });
  }, [api, showExampleUi]);

  return (
    <Container fluid>
      <Header as="h2" textAlign="center">
        REST APIs
      </Header>
      <Header dividing>Introduction</Header>
      The <Link to="/apis">REST APIs</Link> page will have the comprehensive
      information about the Swagger definitions of all the REST APIs in the AWS
      API Gateway. The APIs are categorized based on the modules to which the
      APIs belong. The list of modules can be seen in the left sidebar.
      <br />
      <br />
      If you click on the the desired module's name in the sidebar, the list of
      APIs of the module will be expanded down and if you click on the desired
      API name, the Swagger definition of the API will be visualized in the
      right side of the page.
      <br />
      <br />
      The users can download the Swagger API SDK by clicking on the dropdown{" "}
      <b>Download SDK</b> and selecting the SDK language and the API definition
      file by clicking on the dropdown button <b>Download API</b> and selecting
      the file type (json or yaml). A popup will open up asking the file format.
      The file can be downloaded either as Swagger or Open API 3.
      <Header dividing>Swagger</Header>A Swagger (also known as OpenAPI
      Specification) is a framework and language-agnostic standard used for
      designing, documenting, and consuming RESTful APIs. Here's a breakdown of
      what Swagger is and does:
      <Header.Subheader as="h3">Description of Swagger</Header.Subheader>
      Swagger is a set of open-source tools and a specification used to:
      <List.Item as="li">
        <b>Describe REST APIs:</b> Using a standardized format (usually a YAML
        or JSON file), Swagger defines endpoints, HTTP methods, input/output
        parameters, response schemas, authentication methods, and more.
      </List.Item>
      <List.Item as="li">
        <b> Generate Interactive API Documentation:</b> Swagger UI provides a
        web-based UI that displays the API and allows developers or users to
        test API endpoints directly from the browser.
      </List.Item>
      <List.Item as="li">
        <b>Generate Client and Server Code:</b> With Swagger Codegen or OpenAPI
        Generator, developers can auto-generate server stubs and client SDKs in
        multiple programming languages.
      </List.Item>
      <List.Item as="li">
        <b>Validate API Design:</b> Ensures consistency and correctness before
        or after implementation.
      </List.Item>
      <Header.Subheader as="h3">Key Components</Header.Subheader>
      <List as="ol">
        <List.Item as="li">
          <b>Swagger/OpenAPI Specification File:</b> A .yaml or .json file
          describing the API.
        </List.Item>
        <List.Item as="li">
          <b>Swagger UI:</b> A tool to render the specification as interactive
          documentation.
        </List.Item>
        <List.Item as="li">
          <b>Try it out feature:</b> Allows users to make test API calls
          directly from the browser.
        </List.Item>
        <List.Item as="li">
          <b>Swagger Editor:</b> A browser-based editor for creating and editing
          API specs.
        </List.Item>
        <List.Item as="li">
          <b>Swagger Codegen:</b> Tool to generate client libraries and server
          stubs automatically.
        </List.Item>
      </List>
      <Header dividing>Example</Header>
      <Segment.Group>
        <Segment>
          <Header as="h4" onClick={() => setShowExampleSnippet((s) => !s)}>
            Snippet(yaml){" "}
            <Icon name={`${showExampleSnippet ? "angle up" : "angle down"}`} />
          </Header>
          {showExampleSnippet && (
            <ReactCodeMirror
              editable={false}
              value={api}
              extensions={[yaml()]}
            />
          )}
        </Segment>
        <Segment>
          <Header as="h4" onClick={() => setShowExampleUi((s) => !s)}>
            Swagger UI{" "}
            <Icon name={`${showExampleUi ? "angle up" : "angle down"}`} />
          </Header>
          {showExampleUi && (
            <div
              className="swagger-ui-wrap"
              ref={containerRef}
              style={{ padding: "0 20px" }}
            ></div>
          )}
        </Segment>
      </Segment.Group>
    </Container>
  );
}

export default RestApis;
